import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Modal } from '../common/Modal';
import { FileUploadZone } from '../pilgrimGuide/upload/FileUploadZone';
import { ValidationErrors } from '../pilgrimGuide/upload/ValidationErrors';
import { WarningMessage } from '../pilgrimGuide/upload/WarningMessage';
import { Download, Info } from 'lucide-react';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  tableId: string;
};

// Maximum allowed file size (5MB)
const MAX_FILE_SIZE = 5 * 1024 * 1024;

export const EasyBulkAccommodationModal = ({ isOpen, onClose, tableId }: Props) => {
  const { translations } = useLanguage();
  const [file, setFile] = useState<File | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const navigate = useNavigate();
  const [roomTextFilter, setRoomTextFilter] = useState<string>(''); // State for room text filter
  const [sameOrganizerOnly, setSameOrganizerOnly] = useState<boolean>(false); // New state for same organizer option

  const handleDownloadTemplate = () => {
    const template = [
      {
        passport: '123',
        family_link: '1'
      },
      {
        passport: '124',
        family_link: '1'
      },
      {
        passport: '125',
        family_link: '1'
      },
      {
        passport: '126',
        family_link: '1'
      },
      {
        passport: '176',
        family_link: ''
      },
      {
        passport: '178',
        family_link: ''
      }
    ];

    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    XLSX.writeFile(wb, 'easy-bulk-accommodation-template.xlsx');
  };

  const handleUpload = () => {
    if (!file) return;
    
    // Check file size before processing
    if (file.size > MAX_FILE_SIZE) {
      setValidationErrors([`File size exceeds the maximum limit of ${MAX_FILE_SIZE / (1024 * 1024)}MB. Please use a smaller file.`]);
      return;
    }
    
    setIsProcessing(true);
    setValidationErrors([]);

    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        if (!data) {
          throw new Error('Failed to read file data');
        }
        
        // Parse the Excel file
        const workbook = XLSX.read(data, { type: 'array' });
        
        // Validate workbook structure
        if (!workbook || !workbook.SheetNames || workbook.SheetNames.length === 0) {
          setValidationErrors(['Invalid Excel file or empty workbook']);
          setIsProcessing(false);
          return;
        }
        
        // Get first sheet
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        if (!sheet) {
          setValidationErrors(['No sheet found in the workbook']);
          setIsProcessing(false);
          return;
        }
        
        // Get headers from first row and normalize them
        const headerRow = XLSX.utils.sheet_to_json(sheet, { header: 1 })[0];
        
        if (!headerRow || headerRow.length === 0) {
          setValidationErrors(['No headers found in the file']);
          setIsProcessing(false);
          return;
        }
        
        // Normalize header names (lowercase, trim whitespace)
        const headers = headerRow.map(header => 
          typeof header === 'string' ? header.toLowerCase().trim() : String(header).toLowerCase().trim()
        );
        
        // Check if required columns exist (case-insensitive)
        const passportIndex = headers.findIndex(h => h === 'passport');
        const familyLinkIndex = headers.findIndex(h => h === 'family_link');
        
        if (passportIndex === -1 || familyLinkIndex === -1) {
          setValidationErrors([
            'File must contain "passport" and "family_link" columns. ' +
            `Found columns: ${headers.join(', ')}`
          ]);
          setIsProcessing(false);
          return;
        }
        
        // Convert to JSON with proper options - using smaller chunks to reduce memory usage
        const rows = [];
        const range = XLSX.utils.decode_range(sheet['!ref'] || 'A1');
        const rowCount = range.e.r;
        
        // Skip header row (i=1)
        for (let i = 1; i <= rowCount; i++) {
          const row: Record<string, string> = {};
          
          // Only get the passport and family_link columns
          const passportCell = XLSX.utils.encode_cell({r: i, c: passportIndex});
          const familyLinkCell = XLSX.utils.encode_cell({r: i, c: familyLinkIndex});
          
          const passportValue = sheet[passportCell]?.v || '';
          const familyLinkValue = sheet[familyLinkCell]?.v || '';
          
          row.passport = String(passportValue).trim();
          row.family_link = String(familyLinkValue === undefined ? '' : familyLinkValue).trim();
          
          // Only include rows that have a passport number
          if (row.passport) {
            rows.push(row);
          }
        }
        
        if (rows.length === 0) {
          setValidationErrors(['No valid data rows found in the file']);
          setIsProcessing(false);
          return;
        }
        
        // Success - close modal and navigate
        onClose();
        
        // Create a new workbook with normalized data
        const normalizedSheet = XLSX.utils.json_to_sheet(rows);
        const normalizedWb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(normalizedWb, normalizedSheet, 'Data');
        
        // Get binary string from normalized workbook
        const normalizedBinaryString = XLSX.write(normalizedWb, {
          bookType: 'xlsx', 
          type: 'binary'
        });
        
        // Navigate to status page with normalized file data, room text filter, and sameOrganizerOnly flag
        navigate('/bulk-easy-accommodation-status', {
          state: { 
            fileData: normalizedBinaryString,
            tableId,
            roomTextFilter: roomTextFilter.trim(),
            sameOrganizerOnly // Pass the same organizer flag
          }
        });
      } catch (error) {
        console.error('Error processing file:', error);
        setValidationErrors([
          `Error processing file: ${error instanceof Error ? error.message : 'Unknown error'}. Try with a smaller file or check the file format.`
        ]);
        setIsProcessing(false);
      }
    };
    
    reader.onerror = () => {
      setValidationErrors(['Error reading file']);
      setIsProcessing(false);
    };
    
    // Changed from readAsBinaryString to readAsArrayBuffer for better memory efficiency
    reader.readAsArrayBuffer(file);
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose}
      title={translations?.easyBulkAccommodation?.title || "Easy Bulk Accommodation"}
    >
      <div className="space-y-6">
        {/* Instructions */}
        <div className="bg-blue-50 p-4 rounded-lg text-sm">
          <div className="flex items-start gap-3">
            <Info className="text-blue-600 shrink-0 mt-1" size={20} />
            <div>
              <h4 className="font-medium text-blue-800 mb-2">How it works:</h4>
              <ul className="space-y-2 text-blue-700">
                <li>• Upload a file with passport numbers and family links</li>
                <li>• Pilgrims with the same family link number will be placed in the same room</li>
                <li>• Individuals should have an empty family link field</li>
                <li>• The system will automatically find suitable rooms without date conflicts</li>
                <li>• Rooms are assigned from smallest to largest room numbers</li>
                <li>• Maximum file size: 5MB</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Room Text Filter */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Room Text Filter (Optional)
          </label>
          <input
            type="text"
            value={roomTextFilter}
            onChange={(e) => setRoomTextFilter(e.target.value)}
            placeholder="E.g. 'DV' to only assign to rooms containing 'DV'"
            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
          />
          <p className="text-sm text-gray-500">
            If provided, accommodation will only consider rooms that contain this text in their room numbers.
            Leave empty to consider all rooms.
          </p>
        </div>

        {/* Same Organizer Only Option */}
        <div className="space-y-2">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="sameOrganizerOnly"
              checked={sameOrganizerOnly}
              onChange={(e) => setSameOrganizerOnly(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <label htmlFor="sameOrganizerOnly" className="ml-2 block text-sm font-medium text-gray-700">
              Require same organizer for shared rooms
            </label>
          </div>
          <p className="text-sm text-gray-500">
            When enabled, pilgrims will only be assigned to rooms with other pilgrims from the same organizer 
            when there aren't enough pilgrims to fill the entire room.
          </p>
        </div>

        {/* Template Download Button */}
        <button
          onClick={handleDownloadTemplate}
          className="flex items-center gap-2 text-primary-600 hover:text-primary-700"
        >
          <Download size={20} />
          <span>{translations?.easyBulkAccommodation?.downloadTemplate || "Download Excel Template"}</span>
        </button>

        {/* File Upload */}
        <FileUploadZone
          file={file}
          onFileSelect={(selectedFile) => {
            setFile(selectedFile);
            setValidationErrors([]);
          }}
          onFileRemove={() => {
            setFile(null);
            setValidationErrors([]);
          }}
        />

        <ValidationErrors errors={validationErrors} />

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            {translations?.easyBulkAccommodation?.cancel || "Cancel"}
          </button>
          <button
            onClick={handleUpload}
            disabled={isProcessing || !file}
            className={`px-6 py-2 rounded-lg text-white
              ${isProcessing || !file
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-primary-500 hover:bg-primary-600'}`}
          >
            {isProcessing 
              ? (translations?.easyBulkAccommodation?.processing || "Processing...") 
              : (translations?.easyBulkAccommodation?.upload || "Upload")}
          </button>
        </div>
      </div>
    </Modal>
  );
};
